<template>
  <v-dialog
    width="700"
    persistent
    v-model="dialog"
  >
    <v-form @submit.prevent.stop="createSite()">
      <v-card>
        <v-card-title>
          Nouveau Site
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5" v-if="isLoaded">
          <v-text-field
            v-model="name"
            hide-details
            outlined
            label="Nom du site *"
            :error="nameError"
            class="mb-2 pb-2"
            autofocus
            v-if="dialog"
          />
          <v-textarea
            label="Adresse"
            v-model="address"
            hide-details
            outlined
            append-icon=""
            height="100"
            class="mb-2 pb-2"
          />
          <v-row no-gutters justify="center" align="center" class="mb-2">
            <v-col>
              <v-text-field
                v-model="postalCode"
                hide-details
                outlined
                label="Code postal"
                class="mr-2"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="city"
                hide-details
                outlined
                label="Ville"
                class="ml-2"
              />
            </v-col>
          </v-row>
          <template v-if="isRealEstate">
            <v-row no-gutters justify="center" align="center" class="mb-2 pa-2">
              <v-text-field
                v-model="tenant"
                label="Occupant"
                outlined
                required
                hide-details
                color="primary darken-1"
              />
            </v-row>
            <v-row no-gutters justify="center" align="center" class="mb-2 pa-2">
              <v-text-field
                v-model="owner"
                label="Priopriétaire"
                outlined
                required
                hide-details
                color="primary darken-1"
              />
            </v-row>
          </template>
          <template v-if="areClientTagsActivated">
            <div class="pl-1 pt-3">
              <div class="text-subtitle-1 grey--text text--darken-3">Tags</div>
            </div>
            <v-row no-gutters justify="start" align="center" class="pt-2">
              <tag-chip v-for="tag in tags" :key="tag.id" :tag="tag" @remove="removeTag" removable />
              <div>
                <tags-dialog-list
                  :client-id="tagsClientId"
                  :maker-id="makerId"
                  :excluded-tags="tags"
                  @selected="tagSelected"
                />
              </div>
            </v-row>
          </template>
          <template v-if="areMakerTagsActivated">
            <div class="text-subtitle-1 grey--text text--darken-3">Tags</div>
            <tag-chip v-for="tag in tags" :key="tag.id" :tag="tag" @remove="removeTag" removable />
            <div>
              <tags-dialog-list
                :client-id="tagsClientId"
                :maker-id="makerId"
                :excluded-tags="tags"
                @selected="tagSelected"
              />
            </div>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer></v-spacer>
          <v-btn type="submit" :loading="isCreating" color="primary darken-1" class="px-4">Créer ce site</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import _ from 'lodash';
import api from '@/api';
import { mapActions, mapState } from 'vuex';

export default {
  props: {
    defaultName: {
      type: String,
      default: ``,
    },
    makerId: {
      type: Number,
      required: false,
    },
    clientId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    dialog: true,
    isShowingTagsList: false,
    isLoaded: false,
    isCreating: false,
    nameError: false,
    domains: [],
    clientTags: [],
    makerTags: [],
    name: ``,
    address: ``,
    domainId: null,
    postalCode: ``,
    city: ``,
    tenant: ``,
    owner: ``,
  }),
  computed: {
    ...mapState(`model/user`, [
      `isMakerUser`,
      `isAggregatorUser`,
    ]),
    ...mapState(`model/client`, [
      `isRealEstate`,
      `isMaker`,
      `isAggregator`,
    ]),
    canSetDefaultDomain() {
      return this.makerId && this.makerId !== -1;
    },
    tags: {
      get() {
        if (this.makerId) {
          return this.makerTags;
        }

        return this.clientTags;
      },
      set(tags) {
        if (this.makerId) {
          this.makerTags = tags;
        } else {
          this.clientTags = tags;
        }
      },
    },
    tagsClientId() {
      if (this.makerId) {
        return null;
      }

      return this.clientId;
    },
    areClientTagsActivated() {
      return Boolean(this.isAggregator && this.isAggregatorUser);
    },
    areMakerTagsActivated() {
      return Boolean(this.isMaker && this.isMakerUser);
    },
  },
  methods: {
    ...mapActions(`model/site`, {
      storeCreateSite: `createSite`,
    }),
    tagSelected(tag) {
      this.tags.push(tag);
    },
    removeTag(tag) {
      this.tags = _.reject(this.tags, { id: tag.id });
    },
    async createSite() {
      if (this.isCreating) { return; }

      this.nameError = false;
      this.domainError = false;
      if (!this.name.length) {
        this.nameError = true;
        return;
      }

      this.isCreating = true;
      const domain = _.find(this.domains, { id: this.domainId });
      const site = await this.storeCreateSite({
        Client_id: parseInt(this.clientId, 10),
        Maker_id: this.makerId ? parseInt(this.makerId, 10) : null,
        name: this.name,
        address: this.address,
        postalCode: this.postalCode,
        city: this.city,
        tenant: this.tenant,
        owner: this.owner,
        email: ``,
        Domain_id: domain ? parseInt(domain.id, 10) : null,
        clientTags: this.clientTags,
        makerTags: this.makerTags,
      });

      if (site) {
        this.$emit(`created`, site);
      }

      this.isCreating = false;
    },
  },
  watch: {
    dialog(isOpened) {
      if (!isOpened) {
        this.$emit(`close`);
      }
    },
  },
  async beforeMount() {
    this.isLoaded = false;
    this.isCreating = false;
    this.name = this.defaultName;

    if (this.canSetDefaultDomain) {
      const { data: domains } = await api.get(`/domains/maker/${this.makerId}`);
      this.domains = domains;
    }

    this.isLoaded = true;
  },
};
</script>
